import { Box, IconButton } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { ColumnDef } from "@tanstack/react-table"

import { SalesOpportunityStatusChip } from "../components/SalesOpportunityStatusChip"

import { Button } from "@/components/common/Button"
import DateChip from "@/components/DateChip"
import { ProductStatusCell } from "@/components/table"
import type { Product, SalesPipeline } from "@/graphql/codegen/graphql"

export const useSalesOpportunityColumns = (): ColumnDef<SalesPipeline>[] => {
  const navigate = useNavigate()

  const handleViewQuote = (quoteId: string) => {
    navigate({
      to: "/app/pricing/$priceChangeId/review",
      params: { priceChangeId: quoteId },
    })
  }

  return [
    {
      accessorKey: "code",
      header: "Opportunity ID",
      cell: ({ row }) => row.original.code,
      enableSorting: true,
    },
    {
      accessorKey: "customer",
      header: "Customer",
      cell: ({ row }) => row.original.company?.name ?? null,
      enableSorting: true,
    },
    {
      accessorKey: "createdBy",
      header: "Created by",
      cell: ({ row }) => row.original.createdByUser?.name ?? null,
      enableSorting: false,
    },
    {
      accessorKey: "product",
      header: "Product",
      cell: ({ row }) =>
        row.original.product ? <ProductStatusCell product={row.original.product as Product} /> : null,
      enableSorting: false,
    },
    {
      accessorKey: "productDescription",
      header: "Product Desc.",
      cell: ({ row }) => row.original.product?.companyProductDescription ?? null,
      enableSorting: false,
    },
    {
      accessorKey: "quoteDate",
      header: "Quote",
      cell: ({ row }) => (row.original.quoteDate ? <DateChip date={row.original.quoteDate} /> : null),
      enableSorting: true,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <SalesOpportunityStatusChip status={row.original.status} />,
      enableSorting: true,
    },

    {
      accessorKey: "viewQuote",
      header: "",
      size: 120,
      enableSorting: false,
      cell: ({ row }) => {
        const priceChangeSubmissionId = row.original?.latestQuote?.priceChangeSubmissionId

        if (!priceChangeSubmissionId) return null

        return (
          <Button
            appearance='text'
            size='small'
            className='hover:bg-transparent'
            onClick={() => handleViewQuote(priceChangeSubmissionId)}
            data-testid={`sales-opportunity.view-quote-button-${row.original.id}`}
          >
            View Quote
          </Button>
        )
      },
    },

    {
      accessorKey: "expander",
      header: "",
      size: 50,
      enableSorting: false,
      cell: ({ row }) => (
        <Box className='flex items-center justify-end'>
          <IconButton
            size='small'
            onClick={row.getToggleExpandedHandler()}
            data-testid={`sales-opportunity.row-actions.expand-button-${row.original.id}`}
          >
            {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Box>
      ),
    },
  ]
}
